import { useMemo } from "react";
import { optionalNonNegativeNumber, requireNonNegativeNumber, requireNotFalsy } from "./utils/commonValidations";
import { getHighestLevelWithPrefix } from "./utils/parseProblems";

// type FormProblem = {
//   // Represents where to display message. If undefined, displays at bottom of form.
//   field?: string;
//   message: string;
//   level: "warning" | "incomplete" | "error" | "info";
//   displayLevel: "warning" | "incomplete" | "error";
// };

/**
 *
 * @param {{[key: any]: value}[]} data - An array of items to validate.
 * @returns
 */
export function validateWater(data) {
  return data?.flatMap((meter) => {
    const problems = [];

    requireNonNegativeNumber(problems, meter.Reading, `${meter.Id}-reading`);
    requireNotFalsy(problems, meter.Date, `${meter.Id}-date`);

    return problems;
  }) ?? [];
}

export function validateCrops(data, tractAcres) {
  let totalIrrigation = 0;

  if ((data?.length ?? 0) === 0) {
    return [{
      id: 'no-crops',
      hidden: true,
      message: 'At least one crop is required',
      level: 'incomplete',
    }];
  }

  return data.flatMap((crop, i) => {
    const problems = [];

    requireNotFalsy(problems, crop.Crop_Planted, `${crop._id}-crop-name`);
    requireNonNegativeNumber(problems, crop.Crop_Yield, `${crop._id}-crop-yield`);
    requireNotFalsy(problems, crop.Crop_Yield_Units, `${crop._id}-crop-units`);

    requireNonNegativeNumber(problems, crop.Year, `${crop._id}-crop-year`);

    optionalNonNegativeNumber(problems, crop.Irrigation_Water_Applied, `${crop._id}-crop-irr-applied`);
    optionalNonNegativeNumber(problems, crop.Water_Nitrate, `${crop._id}-crop-water-nitrate`);

    optionalNonNegativeNumber(problems, crop.Sprinkler_Acres, `${crop._id}-crop-sprinkler`);
    optionalNonNegativeNumber(problems, crop.Flood_Acres, `${crop._id}-crop-flood`);
    optionalNonNegativeNumber(problems, crop.Other_Irr_Acres, `${crop._id}-crop-other`);

    const min0 = (v) => Math.max(0, v || 0);

    totalIrrigation += min0(crop.Sprinkler_Acres) + min0(crop.Flood_Acres) + min0(crop.Other_Irr_Acres);
    // If we're on the last crop, check the total irrigation.
    if (i === data.length - 1) {
      if (totalIrrigation === 0) {
        problems.push({
          id: "total-tract-irrigation",
          message: "No irrigation entered for tract",
          level: "incomplete",
        });
      } else if (totalIrrigation < (tractAcres / 2)) {
        // NOTE: Maybe remove this if this is a common case. Could also add a secondary message field
        //       that explains that the warning is fine and we're just trying to be helpful with this
        problems.push({
          id: "total-tract-irrigation",
          message: "Total tract irrigation is low",
          level: "info",
        });
      }
    }

    // NOTE: Special cases for corn.
    if (crop.Crop_Planted === "Corn") {
      requireNotFalsy(problems, crop.Yield_Goal, `${crop._id}-crop-yield-goal`);

      if ((crop.Nitrogen_Applications?.length ?? 0) === 0) {
        problems.push({
          id: `${crop._id}-missing-nitro-application`,
          message: `Crop ${i + 1} ${crop.Crop_Planted ? `(${crop.Crop_Planted}) ` : ""} missing nitrogen applications`,
          level: "incomplete",
        });
      }
    }

    crop.Manure_Applications?.forEach((app) => {
      if (app.deleted) return;

      const appPrefix = `${crop._id}-manure-${app._id}`;

      if (!app.date_applied) {
        problems.push({ field: `${appPrefix}-date`, message: "Required Field", level: "error" });
      } else if ((app.years_ago_applied ?? -1) < 0) {
        problems.push({
          field: `${appPrefix}-date`,
          message: "Date is too far in the future",
          level: "error",
        });
      }

      requireNotFalsy(problems, app.method, `${appPrefix}-method`);
      requireNotFalsy(problems, app.type, `${appPrefix}-type`);

      requireNonNegativeNumber(problems, app.ammonium_amount, `${appPrefix}-amm-nitro`);
      requireNonNegativeNumber(problems, app.amount, `${appPrefix}-amount`);
      requireNonNegativeNumber(problems, app.organic_amount, `${appPrefix}-org-nitro`);
    });

    crop.Nitrogen_Applications?.forEach((app) => {
      if (app.deleted) return;

      const appPrefix = `${crop._id}-nitro-${app._id}`;

      requireNotFalsy(problems, app.date_applied, `${appPrefix}-date`);

      requireNotFalsy(problems, app.emergent_type, `${appPrefix}-emergence`);

      if (app.inhibitor_used) {
        requireNotFalsy(problems, app.inhibitor_type, `${crop._id}-nitro-${app._id}-emergence`);
      }

      // This logic is necessarily circular, we can't know anything until we know everything.
      if (app.direct_nitrogen !== "" && app.direct_nitrogen != null) {
        // If there's direct nitrogen, this is definitely NOT advanced.
        if (app.direct_nitrogen < 0) {
          requireNonNegativeNumber(problems, app.direct_nitrogen, `${appPrefix}-nitrogen-applied`);
        }
      } else {
        // At this point we don't know if this is advanced or not.

        // Track problems in here as if we think advanced fields are required.
        const subProblems = [];
        // This tracks if any advanced field is present.
        let hasAnyAdvancedField = false;

        if (app.amount !== "" && app.amount != null) {
          hasAnyAdvancedField = true;
          requireNonNegativeNumber(subProblems, app.amount, `${appPrefix}-amount`);
        } else {
          subProblems.push({
            field: `${appPrefix}-amount`,
            message: "Required Field",
            level: "error",
          });
        }

        if (app.n_density !== "" && app.n_density != null) {
          hasAnyAdvancedField = true;
          requireNonNegativeNumber(subProblems, app.density, `${appPrefix}-density`);
        } else {
          subProblems.push({
            field: `${appPrefix}-density`,
            message: "Required Field",
            level: "error",
          });
        }

        if (app.k_percent !== "" && app.k_percent != null) {
          hasAnyAdvancedField = true;
          requireNonNegativeNumber(subProblems, app.k_percent, `${appPrefix}-k-percent`);
        } else {
          subProblems.push({
            field: `${appPrefix}-k-percent`,
            message: "Required Field",
            level: "error",
          });
        }

        if (app.n_percent !== "" && app.n_percent != null) {
          hasAnyAdvancedField = true;
          requireNonNegativeNumber(subProblems, app.n_percent, `${appPrefix}-n-percent`);
        } else {
          subProblems.push({
            field: `${appPrefix}-n-percent`,
            message: "Required Field",
            level: "error",
          });
        }

        if (app.p_percent !== "" && app.p_percent != null) {
          hasAnyAdvancedField = true;
          requireNonNegativeNumber(subProblems, app.p_percent, `${appPrefix}-p-percent`);
        } else {
          subProblems.push({
            field: `${appPrefix}-p-percent`,
            message: "Required Field",
            level: "error",
          });
        }

        if (app.type !== "") {
          hasAnyAdvancedField = true;
          // No other validation needed for type.
        } else {
          subProblems.push({
            field: `${appPrefix}-type`,
            message: "Required Field",
            level: "error",
          });
        }

        if (app.units !== "") {
          hasAnyAdvancedField = true;
          // No other validation needed for units.
        } else {
          subProblems.push({
            field: `${appPrefix}-units`,
            message: "Required Field",
            level: "error",
          });
        }

        // NOTE: This logic is only sufficient for validation for submission. The form
        //       itself checks the isAdvanced toggle and displays errors appropriately
        if (hasAnyAdvancedField) {
          // If any single advanced field is filled in, we need all subProblems.
          problems.push(...subProblems);
        } else {
          // Otherwise, mark nitrogen applied as required field and disregard subProblems.
          problems.push({
            field: `${appPrefix}-nitrogen-applied`,
            message: "Required Field",
            level: "error",
          });
        }
      }
    });

    return problems;
  }) ?? [];
}

export function validateSoil(data, tractAcres, nrd) {
  if ((data?.length ?? 0) === 0) {
    // FIXME: This should be part of the configuration when that's implemented. This is temporary.
    if (nrd === 'LC') {
      return [{
        id: 'no-samples',
        hidden: true,
        message: 'At least one soil sample is required',
        level: 'info',
        displayLevel: 'incomplete',
      }];
    }

    return [{
      id: 'no-samples',
      hidden: true,
      message: 'At least one soil sample is required',
      level: 'incomplete',
    }];
  }

  return data.flatMap((sample) => {
    const prefix = `soil-${sample._id}`;

    const problems = [];

    requireNotFalsy(problems, sample.sample_id, `${prefix}-name`);
    requireNotFalsy(problems, sample.sample_date, `${prefix}-sample-date`);
    requireNotFalsy(problems, sample.lab_name, `${prefix}-lab`);
    requireNotFalsy(problems, sample.lab_date, `${prefix}-report-date`);
    requireNotFalsy(problems, sample.soil_texture, `${prefix}-texture`);

    // TODO: Acres probably also needs to be less than tract acres, but I would
    //       hesitate to force the sum of all acres to be less than tract acres
    //       in case its possible to have overlap in sample acre representation
    requireNonNegativeNumber(problems, sample.acres_represented, `${prefix}-acres`);

    if ((sample.soil_layers?.length ?? 0) === 0) {
      problems.push({
        field: `${prefix}-no-layers-added`,
        level: "warning",
        message: "At least one soil layer is required per sample",
      });
    }

    let accDepth = 0;

    // Make sure we check depths in the right order.
    sample.soil_layers?.sort((a, b) => a.layer_num - b.layer_num);
    sample.soil_layers?.forEach((layer) => {
      if (layer.deleted) return;

      const layerPrefix = `${prefix}-layer-${layer.layer_num}`;

      const top = layer.top_depth;
      const bottom = layer.bottom_depth;

      // Depths are required to make sense and be in order.
      if (top === "" || top < 0) {
        problems.push({
          field: `${layerPrefix}-top-depth`,
          level: "error",
          message: top === "" ? "Required Field" : "Value cannot be negative",
        });
      } else if (top < accDepth) {
        problems.push({
          field: `${layerPrefix}-top-depth`,
          level: "error",
          message: "Cannot be above previous layer",
        });
      }

      if (bottom === "" || bottom < 0) {
        problems.push({
          field: `${layerPrefix}-bottom-depth`,
          level: "error",
          message: bottom === "" ? "Required Field" : "Value cannot be negative",
        });
      } else if (bottom < top) {
        problems.push({
          field: `${layerPrefix}-bottom-depth`,
          level: "error",
          message: "Cannot be above top of layer",
        });
      }

      accDepth = bottom || accDepth;

      requireNonNegativeNumber(problems, layer.nitrate, `${layerPrefix}-nitrate`);

      optionalNonNegativeNumber(problems, layer.calcium, `${layerPrefix}-calcium`);
      optionalNonNegativeNumber(problems, layer.cec, `${layerPrefix}-cations`);
      optionalNonNegativeNumber(problems, layer.magnesium, `${layerPrefix}-magnesium`);

      // Organic matter is only required for the first layer (per PROC-434)
      if (layer.layer_num === 1) {
        requireNonNegativeNumber(problems, layer.organic_matter, `${layerPrefix}-organic-matter`);
      } else {
        // Otherwise we can allow "", but still not negative numbers.
        optionalNonNegativeNumber(problems, layer.organic_matter, `${layerPrefix}-organic-matter`);
      }

      optionalNonNegativeNumber(problems, layer.ph, `${layerPrefix}-ph`);
      optionalNonNegativeNumber(problems, layer.phosphate, `${layerPrefix}-phosphate`);
      optionalNonNegativeNumber(problems, layer.potassium, `${layerPrefix}-potassium`);
      optionalNonNegativeNumber(problems, layer.sodium, `${layerPrefix}-sodium`);
      optionalNonNegativeNumber(problems, layer.soluble_salts, `${layerPrefix}-soluble-salts`);
      optionalNonNegativeNumber(problems, layer.sulfate, `${layerPrefix}-sulfate`);
    });

    return problems;
  }) ?? [];
}

function problemsToStatus(problems) {
  if (problems.length === 0) {
    return { level: "complete", displayLevel: "complete" };
  }

  return {
    level: getHighestLevelWithPrefix(problems, "") || "complete",
    displayLevel: getHighestLevelWithPrefix(problems, "", true) || "complete",
  };
}

function checkWaterStatus(data) {
  const p = validateWater(data);
  return [p, problemsToStatus(p)];
}

function checkCropsStatus(data, totalAcreage) {
  const p = validateCrops(data, totalAcreage);
  console.log('ASDASDASDASD', p);
  return [p, problemsToStatus(p)];
}

function checkSoilStatus(data, totalAcreage, nrd) {
  const p = validateSoil(data, totalAcreage, nrd);
  return [p, problemsToStatus(p)];
}

export default function useReportValidation(data, showWater, showCrops, showSoil) {
  const [problems, statuses] = useMemo(() => {
    // Unused sections can just be "complete" because they don't
    // render and it makes the "confirm" status easier to check.
    const [waterProblems, waterStatus] = showWater ?
      checkWaterStatus(data?.flowmeter_section) : "complete";
    const [cropsProblems, cropsStatus] = showCrops ?
      checkCropsStatus(data?.crop_section, data?.tract_details.acres) : "complete";
    const [soilProblems, soilStatus] = showSoil ?
      checkSoilStatus(data?.soil_section, data?.tract_details.acres, data?.tract_details._nrd) : "complete";

    const results = [waterStatus, cropsStatus, soilStatus];
    // `confirm` gets the most severe level out of the other 3, and doesn't need a display level.
    let confirm;

    if (results.find((r) => r.level === "error")) {
      confirm = "error";
    } else if (results.find((r) => r.level === "warning")) {
      confirm = "warning";
    } else if (results.find((r) => r.level === "incomplete")) {
      confirm = "incomplete";
    } else {
      confirm = "complete";
    }

    return [
      {
        water: waterProblems,
        crops: cropsProblems,
        soil: soilProblems,
      },
      {
        water: waterStatus,
        crops: cropsStatus,
        soil: soilStatus,
        confirm,
      },
    ];
  }, [data, showWater, showSoil, showCrops]);

  return {
    statuses,
    problems,
  }
}
